import type { JourneyPayload } from '../../../types/analyticstypes';
import { formatRate } from '../../../utils/analytics/format';

interface FunnelSlice {
  source: string;
  impressions: number;
  accepts: number;
}

export default function UpsellFunnelCard({ journey }: { journey: JourneyPayload }) {
  const upsellMap: Record<string, FunnelSlice> = {};
  let addOnsShown = 0;
  let addOnsAccepted = 0;

  for (const ev of journey.semanticEvents || []) {
    if (ev.type === 'upsell_impression') {
      const src = ev.data?.source ? String(ev.data.source) : 'unknown';
      if (!upsellMap[src]) upsellMap[src] = { source: src, impressions: 0, accepts: 0 };
      upsellMap[src].impressions++;
    } else if (ev.type === 'upsell_accept') {
      const src = ev.data?.source ? String(ev.data.source) : 'unknown';
      if (!upsellMap[src]) upsellMap[src] = { source: src, impressions: 0, accepts: 0 };
      upsellMap[src].accepts++;
    } else if (ev.type === 'add_ons_shown') {
      addOnsShown++;
    } else if (ev.type === 'add_ons_dismissed') {
      addOnsAccepted += Number(ev.data?.acceptedCount) || 0;
    }
  }

  const upsells = Object.values(upsellMap);
  const totalUpsellImpressions = upsells.reduce((s, u) => s + u.impressions, 0);
  const totalUpsellAccepts = upsells.reduce((s, u) => s + u.accepts, 0);

  if (totalUpsellImpressions === 0 && addOnsShown === 0) return null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <h4 className="text-sm font-semibold text-gray-800 mb-3">Add-on & Upsell Activity</h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-2">
            Add-ons
          </p>
          {addOnsShown > 0 ? (
            <>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 w-16">Shown</span>
                  <div className="flex-1 bg-emerald-100 rounded-full h-3 relative">
                    <div className="bg-emerald-500 h-3 rounded-full" style={{ width: '100%' }} />
                    <span className="absolute right-2 top-0 text-[10px] font-semibold text-emerald-900 leading-3">
                      {addOnsShown}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 w-16">Accepted</span>
                  <div className="flex-1 bg-emerald-100 rounded-full h-3 relative">
                    <div
                      className="bg-emerald-600 h-3 rounded-full"
                      style={{ width: `${addOnsShown ? (addOnsAccepted / addOnsShown) * 100 : 0}%` }}
                    />
                    <span className="absolute right-2 top-0 text-[10px] font-semibold text-emerald-900 leading-3">
                      {addOnsAccepted}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Acceptance rate:{' '}
                <span className="font-medium text-gray-700">
                  {formatRate(addOnsShown ? (addOnsAccepted / addOnsShown) * 100 : 0)}
                </span>
              </p>
            </>
          ) : (
            <p className="text-xs text-gray-400 italic">No add-on prompts</p>
          )}
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-fuchsia-700 font-semibold mb-2">
            Upsells
          </p>
          {totalUpsellImpressions > 0 ? (
            <>
              <div className="space-y-1.5">
                {upsells.map((u) => (
                  <div key={u.source} className="text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-gray-700 font-medium capitalize">{u.source}</span>
                      <span className="text-gray-500">
                        {u.accepts}/{u.impressions}
                      </span>
                    </div>
                    <div className="bg-fuchsia-100 rounded-full h-2 relative">
                      <div
                        className="bg-fuchsia-500 h-2 rounded-full"
                        style={{ width: `${u.impressions ? (u.accepts / u.impressions) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Overall:{' '}
                <span className="font-medium text-gray-700">
                  {formatRate(
                    totalUpsellImpressions
                      ? (totalUpsellAccepts / totalUpsellImpressions) * 100
                      : 0
                  )}
                </span>{' '}
                ({totalUpsellAccepts}/{totalUpsellImpressions})
              </p>
            </>
          ) : (
            <p className="text-xs text-gray-400 italic">No upsell impressions</p>
          )}
        </div>
      </div>
    </div>
  );
}
